/**
 * The Meta Pixel, the Google tag and Google Analytics 4, loaded only after the visitor presses Accept.
 *
 * Nothing in here runs at import. `loadAdTags()` is called by the consent banner once the choice
 * is "granted", and every tracking helper checks that the tags were actually loaded first, so a
 * visitor who rejected (or never answered) sends nothing to either company.
 *
 * The ids come from public env vars, inlined at build time:
 *   NEXT_PUBLIC_META_PIXEL_ID              the Meta dataset ("Ownly Digital", 831970528048735)
 *   NEXT_PUBLIC_GOOGLE_ADS_ID              "AW-..." once a Google Ads account exists
 *   NEXT_PUBLIC_GOOGLE_ADS_BOOKING_LABEL   the label of the "booked a call" conversion action
 *   NEXT_PUBLIC_GA_MEASUREMENT_ID          the GA4 web stream ("G-..."), added 2026-10-26. Same gtag.js
 *                                          as the Ads tag, same consent: nothing loads before Accept.
 * Any that are unset are skipped. With none set there is no banner at all (see lib/consent.ts).
 *
 * WHY NOT next/script OR @next/third-parties. Both load on mount. The whole point here is that
 * the network request itself waits for a yes, so the scripts are injected by hand after one.
 */

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const META_PIXEL_ID = (process.env.NEXT_PUBLIC_META_PIXEL_ID ?? "").trim();
export const GOOGLE_ADS_ID = (process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "").trim();
export const GOOGLE_ADS_BOOKING_LABEL = (process.env.NEXT_PUBLIC_GOOGLE_ADS_BOOKING_LABEL ?? "").trim();
export const GA_MEASUREMENT_ID = (process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? "").trim();

/** Only digits: a stray quote or space in the env var must not end up inside a script URL. */
export function validMetaPixelId(id: string): boolean {
  return /^\d{6,20}$/.test(id);
}

export function validGoogleAdsId(id: string): boolean {
  return /^AW-\d{6,15}$/.test(id);
}

export function validGaMeasurementId(id: string): boolean {
  return /^G-[A-Z0-9]{4,16}$/.test(id);
}

/** The companies whose tags this build would load, named the way the banner says them. */
export function configuredAdVendors(
  metaId: string = META_PIXEL_ID,
  googleId: string = GOOGLE_ADS_ID,
  gaId: string = GA_MEASUREMENT_ID,
): string[] {
  const vendors: string[] = [];
  if (validMetaPixelId(metaId)) vendors.push("Meta");
  // Google Ads and Google Analytics are one company and one script: name it once.
  if (validGoogleAdsId(googleId) || validGaMeasurementId(gaId)) vendors.push("Google");
  return vendors;
}

/**
 * The id both sides use for one booking, so Meta counts it once.
 *
 * The browser sees the invitee uuid in Calendly's `calendly.event_scheduled` message; Lead Engine
 * sees the same uuid at the end of the invitee uri in the `invitee.created` webhook. Both build
 * this same string, and Meta drops whichever copy of the Schedule event arrives second.
 */
export function bookingEventId(inviteeUuid: string): string {
  return `calendly-${inviteeUuid.toLowerCase()}`;
}

let loaded = false;

function injectScript(src: string): void {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/** Meta's own base code, rewritten as a function so it cannot run before consent. */
function startMetaPixel(id: string): void {
  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    } as Fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
  }
  injectScript("https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", id);
  window.fbq("track", "PageView");
}

/**
 * One gtag.js for both Google products. Ads (when its id is set) gets ad storage as before;
 * Analytics gets analytics storage only, with Google signals and ad personalisation off.
 */
function startGoogleTag(adsId: string | null, gaId: string | null): void {
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // gtag.js reads the `arguments` object itself, not an array, so this must stay a real function.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  /* Consent Mode v2. Declared denied first and then granted, rather than only granted, so that
     Google's own records show the grant as an explicit update from a visitor's choice. */
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  window.gtag(
    "consent",
    "update",
    adsId
      ? { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" }
      : { analytics_storage: "granted" },
  );
  window.gtag("js", new Date());
  if (adsId) window.gtag("config", adsId);
  if (gaId) {
    window.gtag("config", gaId, { allow_google_signals: false, allow_ad_personalization_signals: false });
  }
  const first = adsId ?? gaId;
  if (first) injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(first)}`);
}

/** Starts whichever tags are configured. Call only once the visitor has said yes. */
export function loadAdTags(): void {
  if (loaded || typeof window === "undefined") return;
  loaded = true;
  if (validMetaPixelId(META_PIXEL_ID)) startMetaPixel(META_PIXEL_ID);
  const adsId = validGoogleAdsId(GOOGLE_ADS_ID) ? GOOGLE_ADS_ID : null;
  const gaId = validGaMeasurementId(GA_MEASUREMENT_ID) ? GA_MEASUREMENT_ID : null;
  if (adsId || gaId) startGoogleTag(adsId, gaId);
}

/**
 * Tells the tags already on this page to stop. The page is reloaded straight after a withdrawal,
 * which is what actually unloads them; this covers the moment in between.
 */
export function revokeAdTags(): void {
  if (!loaded) return;
  window.fbq?.("consent", "revoke");
  window.gtag?.("consent", "update", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
}

export function adTagsLoaded(): boolean {
  return loaded;
}

/** A client-side route change. The first page view is sent by `loadAdTags` itself. */
export function trackAdPageView(): void {
  if (!loaded) return;
  window.fbq?.("track", "PageView");
  if (validGoogleAdsId(GOOGLE_ADS_ID)) {
    window.gtag?.("event", "page_view", { send_to: GOOGLE_ADS_ID });
  }
}

/** Someone just booked a call. Sent to both tags, carrying the id Lead Engine uses too. */
export function trackBookingConversion(eventId: string): void {
  if (!loaded) return;
  trackLead("booking");
  window.fbq?.("track", "Schedule", {}, { eventID: eventId });
  if (validGoogleAdsId(GOOGLE_ADS_ID) && GOOGLE_ADS_BOOKING_LABEL) {
    window.gtag?.("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_BOOKING_LABEL}`,
      transaction_id: eventId,
    });
  }
}

/**
 * A lead in Google Analytics: a booked call or a site check request. Sends the event name and
 * which form it was, nothing else: no name, email, phone, answers or booking id.
 */
export function trackLead(formName: string): void {
  if (!loaded || !validGaMeasurementId(GA_MEASUREMENT_ID)) return;
  window.gtag?.("event", "generate_lead", { send_to: GA_MEASUREMENT_ID, form_name: formName });
}
